import { useState } from "react";
import type { CustomerContact, CreateContactInput } from "../types";
import {
  useCustomerContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
  useSetDefaultContact,
  useToggleFavoriteContact,
} from "../api";
import { ContactCard } from "../components/ContactCard";
import { ContactForm } from "../components/ContactForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { CustomerSearch } from "@/features/customers/customer-search";

export function AddressBook() {
  const [search, setSearch] = useState("");
  const [filterFavorites, setFilterFavorites] = useState<boolean | undefined>();
  const [filterLabel, setFilterLabel] = useState<string | undefined>();
  const [filterCustomerId, setFilterCustomerId] = useState<
    string | undefined
  >();
  const [formOpen, setFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<
    CustomerContact | undefined
  >();

  // Fetch contacts for selected customer
  const { data, isLoading } = useCustomerContacts(filterCustomerId || "", {
    search,
    isFavorite: filterFavorites,
    label: filterLabel,
  });

  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();
  const setDefaultMutation = useSetDefaultContact();
  const toggleFavoriteMutation = useToggleFavoriteContact();

  const handleSubmit = async (
    input: CreateContactInput & { customerId?: string }
  ) => {
    console.log("AddressBook handleSubmit called with input:", input);
    try {
      if (editingContact) {
        // Update existing contact
        console.log("Updating contact:", editingContact.id);
        await updateMutation.mutateAsync({
          customerId: editingContact.customerId,
          id: editingContact.id,
          input,
        });
        toast.success("Contact updated", {
          description: "Contact has been updated successfully",
        });
      } else {
        // Create new contact for customer
        if (!input.customerId) {
          console.log("ERROR: No customerId provided");
          toast.error("Please select a customer");
          return;
        }
        console.log("Creating contact for customer:", input.customerId);
        await createMutation.mutateAsync({
          customerId: input.customerId,
          input,
        });
        toast.success("Contact added", {
          description: "Contact has been added for the customer",
        });
      }
      setFormOpen(false);
      setEditingContact(undefined);
    } catch (error: unknown) {
      console.error("Error in handleSubmit:", error);
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error ?? "Failed to save contact");
    }
  };

  const handleEdit = (contact: CustomerContact) => {
    setEditingContact(contact);
    setFormOpen(true);
  };

  const handleDelete = async (contact: CustomerContact) => {
    if (!confirm(`Are you sure you want to delete ${contact.contactName}?`))
      return;

    try {
      await deleteMutation.mutateAsync({
        customerId: contact.customerId,
        id: contact.id,
      });
      toast.success("Contact deleted", {
        description: "Contact has been removed",
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error ?? "Failed to delete contact");
    }
  };

  const handleToggleFavorite = async (contact: CustomerContact) => {
    try {
      await toggleFavoriteMutation.mutateAsync({
        customerId: contact.customerId,
        id: contact.id,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error ?? "Failed to update favorite");
    }
  };

  const handleSetDefault = async (contact: CustomerContact) => {
    try {
      await setDefaultMutation.mutateAsync({
        customerId: contact.customerId,
        id: contact.id,
      });
      toast.success("Default contact updated", {
        description: `${contact.contactName} is now the default contact`,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error ?? "Failed to set default contact");
    }
  };

  const handleAddNew = () => {
    setEditingContact(undefined);
    setFormOpen(true);
  };

  return (
    <div className="container mx-auto py-8 px-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Address Book</h1>
          <p className="text-muted-foreground">
            Manage your saved contacts and addresses
          </p>
        </div>
        <Button onClick={handleAddNew}>
          <Plus className="w-4 h-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <CustomerSearch
          value={filterCustomerId}
          onValueChange={setFilterCustomerId}
          placeholder="All customers"
          className="w-[200px]"
        />

        <Select
          value={filterFavorites?.toString() || "all"}
          onValueChange={(value) =>
            setFilterFavorites(value === "all" ? undefined : value === "true")
          }
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Contacts</SelectItem>
            <SelectItem value="true">Favorites</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filterLabel || "all"}
          onValueChange={(value) =>
            setFilterLabel(value === "all" ? undefined : value)
          }
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Labels</SelectItem>
            <SelectItem value="Home">Home</SelectItem>
            <SelectItem value="Office">Office</SelectItem>
            <SelectItem value="Warehouse">Warehouse</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>

        {(filterCustomerId || filterFavorites || filterLabel) && (
          <button
            onClick={() => {
              setFilterCustomerId(undefined);
              setFilterFavorites(undefined);
              setFilterLabel(undefined);
            }}
            className="text-sm text-muted-foreground hover:text-foreground underline"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Contact List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full text-center text-muted-foreground py-12">
            Loading contacts...
          </div>
        ) : !data?.contacts.length ? (
          <div className="col-span-full text-center text-muted-foreground py-12">
            <p className="text-lg">No contacts found</p>
            <p className="text-sm mt-2">
              {search || filterFavorites || filterLabel
                ? "Try adjusting your filters"
                : "Click 'Add Contact' to create your first contact"}
            </p>
          </div>
        ) : (
          data.contacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleFavorite={handleToggleFavorite}
              onSetDefault={handleSetDefault}
            />
          ))
        )}
      </div>

      {/* Contact Form Dialog */}
      <ContactForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingContact(undefined);
        }}
        onSubmit={handleSubmit}
        initialData={editingContact}
        isLoading={updateMutation.isPending || createMutation.isPending}
      />
    </div>
  );
}
